/**
 * Editor Page - Linkode Markdown A4 編輯器
 *
 * 基於 SPEC-005 EditorShell 的完整編輯器頁面
 * 整合 SPEC-006 背景雲端儲存佇列
 * 設計風格採用 Storybook Landing 設計系統 (Sunset Writer Theme)
 *
 * @module pages/editor
 * @see SPEC-005-web-editor-core
 * @see SPEC-006-background-cloud-save-queue
 */

import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText,
  Minus,
  Plus,
  ChevronDown,
  User,
  LogOut,
  CreditCard,
  Check,
  Upload,
  Printer,
  Code,
  Eye,
  ArrowLeft,
  Pencil,
  Loader2,
  Columns2,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  EditorShell,
  type EditorShellRef,
  type EditorChangePayload,
  type EditorSelection,
  useRemoteCursors,
  useRemoteSelections,
} from '../editor';
import {
  WysiwygPreviewEditor,
  type WysiwygPreviewEditorRef,
} from '../editor/components/WysiwygPreviewEditor';
import {
  useSaveQueue,
  SaveStatusIndicator,
  ConflictDialog,
  DocApiClient,
} from '../features/storage';
import { useRoomConnection, usePresence, ParticipantList } from '../features/collab';
import { useYjsCollaboration } from '../features/collab/yjs';
import { useTypedTranslation } from '../i18n';
import {
  // SPEC-019: AI Button UI Integration
  AIButtonTrigger,
  SuggestionPanel,
  ToastNotification,
  useAiSuggestions,
  useAIButtonShortcut,
  useResponsiveDisplayMode,
  useToast,
  type Toast,
} from '../features/ai-button';
import {
  useAuthStore,
  selectHasHydrated,
  selectIsAuthenticated,
} from '../features/auth/store/authStore';

// Storybook Landing Design System
import { Button, Card } from '../stories/landing';
import '../stories/landing/landing.css';

// ============================================================================
// Constants
// ============================================================================

/** 毫米轉像素的換算常數 (螢幕預覽用) */
const MM_TO_PX = 3.7795;
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

/** 預設 Markdown 內容 */
const DEFAULT_CONTENT = `# 歡迎使用 Linkode 編輯器 📄

這是一個專為 **Markdown 寫作** 設計的編輯器，支援即時預覽與 A4 打印排版。

## 功能特色

### 1. 雙模式編輯
- **原始碼模式**：直接編輯 Markdown 語法
- **預覽模式**：即時渲染 Markdown 內容
- 快捷鍵 \`Ctrl+Shift+M\` 切換模式

### 2. 快捷鍵支援
| 快捷鍵 | 功能 |
|--------|------|
| \`Ctrl+B\` | **粗體** |
| \`Ctrl+I\` | *斜體* |
| \`Ctrl+K\` | [連結](url) |

### 3. 程式碼區塊

\`\`\`typescript
interface User {
  id: string;
  name: string;
  email: string;
}

function greet(user: User): string {
  return \`Hello, \${user.name}!\`;
}
\`\`\`

---

> 開始您的寫作之旅吧！✨
`;

/** 字體定義 */
const FONT_FAMILIES: Record<string, string> = {
  sans: `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
  serif: `"Noto Serif CJK TC", "Songti TC", "SimSun", "Times New Roman", Times, serif`,
  mono: `ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace`,
};

/** 字體 key 列表 */
const FONT_KEYS = ['sans', 'serif', 'mono'] as const;
type FontKey = (typeof FONT_KEYS)[number];

/** 字體翻譯 key mapping */
const FONT_STYLE_KEYS: Record<
  FontKey,
  'editorPage.fontStyle.sans' | 'editorPage.fontStyle.serif' | 'editorPage.fontStyle.mono'
> = {
  sans: 'editorPage.fontStyle.sans',
  serif: 'editorPage.fontStyle.serif',
  mono: 'editorPage.fontStyle.mono',
};

// ============================================================================
// Types
// ============================================================================

interface Margins {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/** 視圖模式：雙欄、僅編輯器、僅預覽 */
type ViewMode = 'both' | 'editor-only' | 'preview-only';

// ============================================================================
// Hooks
// ============================================================================

/**
 * 視圖模式管理 Hook（含 localStorage 持久化）
 * @returns [viewMode, setViewMode] tuple
 */
function useViewMode(): [ViewMode, (mode: ViewMode) => void] {
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem('editor-view-mode');
      if (saved === 'both' || saved === 'editor-only' || saved === 'preview-only') {
        return saved;
      }
    } catch (e) {
      console.warn('Failed to load view mode from localStorage:', e);
    }
    return 'both';
  });

  const updateViewMode = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('editor-view-mode', mode);
    } catch (e) {
      console.warn('Failed to save view mode to localStorage:', e);
    }
  }, []);

  return [viewMode, updateViewMode];
}

// ============================================================================
// Component
// ============================================================================

export function EditorPage() {
  const { t } = useTypedTranslation();
  const navigate = useNavigate();
  const editorRef = useRef<EditorShellRef>(null);
  const wysiwygPreviewRef = useRef<WysiwygPreviewEditorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // 追蹤同步來源，避免無限循環
  const syncSourceRef = useRef<'codemirror' | 'milkdown' | null>(null);

  // 🛡️ 追蹤上次 CodeMirror 變更的時間戳，用於防止快速輸入時的回溯
  // 如果 WYSIWYG 的 onChange 在 CodeMirror 變更後 500ms 內觸發，跳過同步
  // 500ms 可覆蓋長按鍵時首字元與第二字元間的 400-500ms 鍵盤自動重複延遲
  const lastCodeMirrorChangeTimeRef = useRef<number>(0);
  const SYNC_DEBOUNCE_MS = 500; // 500ms 防抖窗口（覆蓋鍵盤長按延遲）

  // URL Parameters
  const { docId: urlDocId } = useParams<{ docId: string }>();
  const docId = urlDocId ?? 'local-doc'; // 若無 docId 則使用本地模式
  const isCloudMode = !!urlDocId; // 是否為雲端模式

  // Auth hydration state - CRITICAL: must wait for hydration before API calls
  const hasHydrated = useAuthStore(selectHasHydrated);
  const email = useAuthStore((state) => state.email);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [initialContent, setInitialContent] = useState<string>(DEFAULT_CONTENT);

  // Import state with overlay
  const [showImportOverlay, setShowImportOverlay] = useState(false);
  const [importProgressText, setImportProgressText] = useState('');
  const [importProgress, setImportProgress] = useState(0);

  // Editor state
  const [lastChange, setLastChange] = useState<EditorChangePayload | null>(null);
  const [selection, setSelection] = useState<EditorSelection | null>(null);
  const [baseVersionId, setBaseVersionId] = useState<string>('initial-version');

  // Ref to track if current save is triggered by import
  const isImportTriggeredSaveRef = useRef(false);

  // UI state
  const [viewMode, updateViewMode] = useViewMode();
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showFontStyleMenu, setShowFontStyleMenu] = useState(false);

  // Document title state
  const [docTitle, setDocTitle] = useState<string>(t('editorPage.untitledDocument'));
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitleValue, setEditingTitleValue] = useState('');

  // Conflict dialog state (SPEC-006)
  const [conflictDocId, setConflictDocId] = useState<string | null>(null);

  // ============================================================================
  // SPEC-019: AI Button UI Integration
  // ============================================================================

  // AI Button panel state
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);
  const aiButtonTriggerRef = useRef<HTMLDivElement>(null);

  // AI Suggestions hook
  const [aiState, aiActions] = useAiSuggestions();

  // Responsive display mode (popover for desktop, drawer for mobile)
  const aiDisplayMode = useResponsiveDisplayMode();

  // Toast notifications
  const { toasts, showToast, dismissToast } = useToast();

  // Keyboard shortcut (Cmd/Ctrl+Shift+A)
  useAIButtonShortcut({
    enabled: isCloudMode && isAuthenticated && !isLoading && !aiState.isLoading,
    onTrigger: useCallback(() => {
      if (!isAiPanelOpen) {
        setIsAiPanelOpen(true);
        // Fetch suggestions when opening panel
        if (aiState.backlinks.length === 0 && aiState.tags.length === 0 && !aiState.isLoading) {
          aiActions.fetchSuggestions(docId);
        }
      } else {
        setIsAiPanelOpen(false);
      }
    }, [
      isAiPanelOpen,
      aiState.backlinks.length,
      aiState.tags.length,
      aiState.isLoading,
      aiActions,
      docId,
    ]),
  });

  // AI Button trigger click handler
  const handleAiButtonClick = useCallback(() => {
    if (!isAiPanelOpen) {
      setIsAiPanelOpen(true);
      // Fetch suggestions when opening panel
      if (aiState.backlinks.length === 0 && aiState.tags.length === 0 && !aiState.isLoading) {
        aiActions.fetchSuggestions(docId);
      }
    } else {
      setIsAiPanelOpen(false);
    }
  }, [
    isAiPanelOpen,
    aiState.backlinks.length,
    aiState.tags.length,
    aiState.isLoading,
    aiActions,
    docId,
  ]);

  // AI Panel close handler
  const handleAiPanelClose = useCallback(() => {
    setIsAiPanelOpen(false);
  }, []);

  // AI Apply handler
  const handleAiApply = useCallback(async () => {
    // Calculate count before applying (selections will be cleared after apply)
    const appliedCount = aiState.selectedBacklinks.size + aiState.selectedTags.size;

    try {
      await aiActions.applySelected(docId);
      setIsAiPanelOpen(false);
      showToast({
        type: 'success',
        message: t('ai.toast.applySuccess', { count: appliedCount }),
        showUndo: true,
        onUndo: () => {
          // TODO: Implement undo via editor history
          showToast({
            type: 'info',
            message: t('ai.toast.undoNotImplemented'),
          });
        },
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : t('ai.toast.applyError'),
      });
    }
  }, [aiActions, docId, showToast, t, aiState.selectedBacklinks.size, aiState.selectedTags.size]);

  // AI Retry handler
  const handleAiRetry = useCallback(() => {
    aiActions.fetchSuggestions(docId);
  }, [aiActions, docId]);

  // AI Feedback handler
  const handleAiFeedback = useCallback(
    (candidateId: string, kind: 'backlink' | 'tag', action: 'up' | 'down') => {
      aiActions.submitFeedback(docId, candidateId, kind, action);
    },
    [aiActions, docId]
  );

  // Compute AI Button state
  const aiButtonState = useMemo(() => {
    if (aiState.isLoading) return 'loading';
    if (aiState.error) return 'error';
    if (aiState.backlinks.length > 0 || aiState.tags.length > 0) return 'hasSuggestions';
    return 'idle';
  }, [aiState.isLoading, aiState.error, aiState.backlinks.length, aiState.tags.length]);

  // Suggestion count for badge
  const aiSuggestionCount = aiState.backlinks.length + aiState.tags.length;

  // Appearance state
  const [fontSize, setFontSize] = useState(16);
  const [fontStyle, setFontStyle] = useState<string>('sans');

  // Margins (mm)
  const [margins] = useState<Margins>({
    top: 25.4,
    bottom: 25.4,
    left: 25.4,
    right: 25.4,
  });

  // Stable callbacks for useSaveQueue (prevent infinite re-renders)
  const getAuthToken = useCallback(async () => {
    const token = useAuthStore.getState().sessionToken;
    return token ?? '';
  }, []);

  const handleSaveConflict = useCallback((docId: string, _serverVersionId: string) => {
    setConflictDocId(docId);
  }, []);

  const handleSaveError = useCallback(
    (error: { type: string; message: string; statusCode?: number }) => {
      console.error('[EditorPage] Save error:', error);
    },
    []
  );

  // Handle save success - update baseVersionId for optimistic locking
  const handleSaveSuccess = useCallback((_docId: string, newVersionId: string) => {
    setBaseVersionId(newVersionId);

    // If this save was triggered by file import, hide overlay
    if (isImportTriggeredSaveRef.current) {
      // eslint-disable-next-line no-console -- Info log for import flow tracking
      console.info('[EditorPage] Import save completed, hiding overlay');
      setImportProgress(100);

      // Short delay to show 100% before hiding
      setTimeout(() => {
        setShowImportOverlay(false);
        setImportProgress(0);
        isImportTriggeredSaveRef.current = false;
      }, 500);
    }
  }, []);

  // Save Queue Hook (SPEC-006)
  const {
    state: saveState,
    save,
    retryNow,
    clearQueue,
  } = useSaveQueue({
    baseUrl: import.meta.env.VITE_API_BASE_URL || 'https://api-staging.getlinkode.com',
    getAuthToken,
    onConflict: handleSaveConflict,
    onError: handleSaveError,
    onSuccess: handleSaveSuccess,
  });

  // Realtime Connection Hook (SPEC-007)
  // 只在雲端模式 + 已登入 + hydration 完成 + 文檔載入完成時自動連線
  const userId = useAuthStore((state) => state.userId);
  const {
    connection,
    state: realtimeState,
    error: realtimeError,
    isDegraded,
  } = useRoomConnection(docId, userId ?? 'anonymous', email?.split('@')[0] ?? 'Guest', {
    autoConnect: isCloudMode && isAuthenticated && hasHydrated && !isLoading,
  });

  // Presence Hook (SPEC-007) - 獲取參與者列表和 Presence 更新方法
  const { participants, updateCursor, updateSelection } = usePresence(connection);

  // EditorView state for Remote Cursor/Selection hooks (SPEC-007)
  // We need to track the EditorView instance to use useRemoteCursors and useRemoteSelections
  const [editorView, setEditorView] =
    useState<ReturnType<NonNullable<EditorShellRef['getView']>>>(null);

  // Update editorView when editorRef is available
  useEffect(() => {
    // Poll for EditorView availability (it may not be ready immediately)
    const checkView = () => {
      const view = editorRef.current?.getView();
      if (view && view !== editorView) {
        setEditorView(view);
      }
    };

    // Check immediately and after a short delay
    checkView();
    const timer = setTimeout(checkView, 100);

    return () => clearTimeout(timer);
  }, [editorView, isLoading]); // Re-check when loading state changes

  // Remote Cursors Hook (SPEC-007) - 顯示其他協作者的游標
  const { getLocalCursorPosition } = useRemoteCursors({
    view: editorView,
    participants,
  });

  // Remote Selections Hook (SPEC-007) - 顯示其他協作者的選取範圍
  const { getLocalSelection } = useRemoteSelections({
    view: editorView,
    participants,
  });

  // Yjs Collaboration Hook (SPEC-008) - CRDT 同步
  // 在雲端模式 + 已登入 + 文檔載入完成時啟用
  const {
    extension: yjsExtension,
    yText,
    isReady: isYjsReady,
    state: yjsState,
    error: yjsError,
    isOffline: isYjsOffline,
  } = useYjsCollaboration(docId, userId ?? 'anonymous', {
    enabled: isCloudMode && isAuthenticated && hasHydrated && !isLoading,
    initialContent: initialContent,
    autoConnect: true,
  });

  // 紀錄是否曾經完成過 Yjs ready，避免 transient syncing 導致 EditorShell key 重建
  const [hasYjsInitialized, setHasYjsInitialized] = useState(false);
  useEffect(() => {
    setHasYjsInitialized(false);
  }, [docId]);
  useEffect(() => {
    if (isYjsReady) {
      setHasYjsInitialized(true);
    }
  }, [isYjsReady]);

  // Log Yjs state changes (DEV only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- Development-only debugging
      console.log('[EditorPage] Yjs state:', yjsState, isYjsReady ? '(ready)' : '(connecting)');
      if (yjsError) {
        console.warn('[EditorPage] Yjs error:', yjsError.message);
      }
    }
  }, [yjsState, yjsError, isYjsReady]);

  // ─────────────────────────────────────────────────────────────────────────
  // Effective Initial Content for EditorShell (SPEC-008 Fix)
  // ─────────────────────────────────────────────────────────────────────────
  // When Yjs is ready, use yText content instead of API content.
  // This ensures CodeMirror and Y.Text are synchronized from the start,
  // preventing the "Cannot read properties of null (reading 'parent')" error
  // that occurs when yCollab is reconfigured with mismatched content.
  const effectiveInitialContent = useMemo(() => {
    if (isYjsReady && yText) {
      const yTextContent = yText.toString();
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Development-only debugging
        console.log('[EditorPage] Using yText content for EditorShell', {
          yTextLength: yTextContent.length,
          initialContentLength: initialContent.length,
          contentMatch: yTextContent === initialContent,
        });
      }
      return yTextContent;
    }
    return initialContent;
  }, [isYjsReady, yText, initialContent]);

  // EditorShell key: changes when Yjs becomes ready to force re-creation
  // This ensures yCollab extension is included from the start, not reconfigured later
  const editorShellKey = useMemo(() => {
    const yjsActive = isCloudMode && (hasYjsInitialized || isYjsReady);
    return yjsActive ? `yjs-${docId}` : `local-${docId}`;
  }, [isCloudMode, hasYjsInitialized, isYjsReady, docId]);

  // Log realtime connection state changes (DEV only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- Development-only debugging
      console.log('[EditorPage] Realtime state:', realtimeState, isDegraded ? '(degraded)' : '');
      if (realtimeError) {
        console.warn('[EditorPage] Realtime error:', realtimeError.message);
      }
    }
  }, [realtimeState, realtimeError, isDegraded]);

  // Document Loading Effect
  useEffect(() => {
    if (!isCloudMode) {
      // 本地模式，使用預設內容
      setInitialContent(DEFAULT_CONTENT);
      setBaseVersionId('local-version');
      return;
    }

    // CRITICAL: Wait for Zustand persist hydration to complete before accessing sessionToken
    // Without this, sessionToken will be null even if user is authenticated
    if (!hasHydrated) {
      return;
    }

    // 雲端模式，從 API 載入文件
    const loadDocument = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const apiClient = new DocApiClient(
          import.meta.env.VITE_API_BASE_URL || 'https://api-staging.getlinkode.com',
          async () => {
            // Get real session token from auth store
            const token = useAuthStore.getState().sessionToken;
            return token ?? '';
          }
        );

        const response = await apiClient.getDoc(docId);
        setInitialContent(response.snapshot.markdown);
        setBaseVersionId(response.versionId);
        // Sync document title from API response
        if (response.meta?.title) {
          setDocTitle(response.meta.title);
        }
        // Clear any stale pending saves for this document to avoid version conflicts
        await clearQueue(docId);
      } catch (error) {
        console.error('[EditorPage] Failed to load document:', error);
        if (error && typeof error === 'object' && 'type' in error) {
          const flushError = error as { type: string; message: string; statusCode?: number };
          if (flushError.statusCode === 404) {
            setLoadError(t('editorPage.error.notFound'));
          } else if (flushError.type === 'unauthorized') {
            setLoadError(t('editorPage.error.unauthorized'));
          } else {
            setLoadError(flushError.message || t('editorPage.error.loadFailed'));
          }
        } else {
          setLoadError(t('editorPage.error.loadFailed'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();
    // Note: t is excluded from deps as useTypedTranslation now uses useCallback for stable reference
    // clearQueue is stable (memoized with useCallback)
  }, [docId, isCloudMode, hasHydrated, clearQueue]);

  // Handlers
  // 🛡️ 正規化 Markdown 內容，移除尾部換行符以避免同步差異
  // Milkdown 的 getMarkdown() 會在文檔末尾添加換行符，導致與 CodeMirror 內容不一致
  const normalizeMarkdown = useCallback((md: string): string => md.replace(/\n+$/, ''), []);

  // Handle CodeMirror changes - sync to WYSIWYG preview
  const handleChange = useCallback(
    (payload: EditorChangePayload) => {
      setLastChange(payload);

      // 🛡️ 記錄 CodeMirror 變更時間戳，用於防止快速輸入時的回溯
      lastCodeMirrorChangeTimeRef.current = Date.now();

      // ✅ 使用正規化比較，避免尾部換行符差異導致無效同步
      const currentPreviewMarkdown = wysiwygPreviewRef.current?.getMarkdown?.();
      if (normalizeMarkdown(currentPreviewMarkdown || '') === normalizeMarkdown(payload.markdown)) {
        // eslint-disable-next-line no-console -- Debug log for sync troubleshooting
        console.debug(
          '[EditorPage] Content identical after normalization, skipping CodeMirror sync'
        );
        return;
      }

      // 同步到 WYSIWYG 預覽（如果不是來自 WYSIWYG 的變更）
      if (syncSourceRef.current !== 'milkdown' && wysiwygPreviewRef.current) {
        syncSourceRef.current = 'codemirror';
        wysiwygPreviewRef.current.setMarkdown(payload.markdown);
        // 🛡️ 延遲重置同步來源，確保超過 SYNC_DEBOUNCE_MS 防抖窗口
        // 使用 setTimeout 而非 requestAnimationFrame，避免 16ms 後過早重置
        setTimeout(() => {
          syncSourceRef.current = null;
        }, SYNC_DEBOUNCE_MS + 50); // 550ms，確保超過 500ms 防抖窗口
      }
    },
    [normalizeMarkdown]
  );

  // Handle WYSIWYG preview changes - sync to CodeMirror
  const handleWysiwygChange = useCallback(
    (markdown: string) => {
      // 🛡️ 防護：如果距離上次 CodeMirror 變更小於 100ms，跳過同步
      // 這可以防止快速輸入時，WYSIWYG 的舊內容覆蓋 CodeMirror 的新內容
      const timeSinceLastChange = Date.now() - lastCodeMirrorChangeTimeRef.current;
      if (timeSinceLastChange < SYNC_DEBOUNCE_MS) {
        // eslint-disable-next-line no-console -- Debug log for sync troubleshooting
        console.debug(
          `[EditorPage] Skipping WYSIWYG sync - within debounce window (${timeSinceLastChange}ms < ${SYNC_DEBOUNCE_MS}ms)`
        );
        return;
      }

      // ✅ 使用正規化比較，避免尾部換行符差異導致無效同步
      const currentMarkdown = editorRef.current?.getMarkdown();
      if (normalizeMarkdown(currentMarkdown || '') === normalizeMarkdown(markdown)) {
        // eslint-disable-next-line no-console -- Debug log for sync troubleshooting
        console.debug('[EditorPage] Content identical after normalization, skipping WYSIWYG sync');
        return;
      }

      // 同步到 CodeMirror（如果不是來自 CodeMirror 的變更）
      if (syncSourceRef.current !== 'codemirror') {
        syncSourceRef.current = 'milkdown';
        if (editorRef.current) {
          editorRef.current.setMarkdown(markdown, { preserveUndo: true });
        }
        // 更新 lastChange 以保持狀態同步
        setLastChange((prev) => ({
          markdown,
          versionVector: (prev?.versionVector ?? 0) + 1,
        }));
        // 🛡️ 延遲重置同步來源，確保超過 SYNC_DEBOUNCE_MS 防抖窗口
        setTimeout(() => {
          syncSourceRef.current = null;
        }, SYNC_DEBOUNCE_MS + 50); // 550ms
      }
    },
    [normalizeMarkdown]
  );

  const handleSelectionChange = useCallback(
    (sel: EditorSelection) => {
      setSelection(sel);

      // 同步游標位置到 Presence (SPEC-007)
      // 只在雲端模式且已連線時同步
      if (isCloudMode && realtimeState === 'connected') {
        // 使用 getLocalCursorPosition 將 offset 轉換為 line/column
        const cursorPosition = getLocalCursorPosition();
        if (cursorPosition) {
          updateCursor(cursorPosition);
        }

        // 使用 getLocalSelection 將 offset 轉換為 Selection
        const localSelection = getLocalSelection();
        updateSelection(localSelection);
      }
    },
    [
      isCloudMode,
      realtimeState,
      updateCursor,
      updateSelection,
      getLocalCursorPosition,
      getLocalSelection,
    ]
  );

  // Save handler - called by EditorShell on content change (SPEC-006)
  // In Yjs mode, use yText.toString() as the authoritative content source
  const handleSave = useCallback(
    (docId: string, markdown: string, versionId: string) => {
      // 🛡️ Protection: Reject invalid temporary versionId
      if (versionId.startsWith('import-') || versionId === 'initial-version') {
        console.warn('[EditorPage] Rejected save with invalid versionId:', versionId);
        return;
      }

      // SPEC-008: In Yjs mode, use Y.Text as the authoritative content source
      // This ensures the saved content is always consistent with the CRDT state
      const contentToSave = isYjsReady && yText ? yText.toString() : markdown;

      save(docId, contentToSave, versionId);
    },
    [save, isYjsReady, yText]
  );

  // Conflict dialog handlers (SPEC-006)
  // SPEC-008: In Yjs mode, conflicts are less likely due to CRDT
  // but API version conflicts can still occur if server state diverges
  const handleConflictRefresh = useCallback(() => {
    // In Yjs mode, a reconnect might be sufficient to resolve conflicts
    // For now, we still refresh the page to ensure clean state
    // Future optimization: attempt Yjs reconnect first
    window.location.reload();
  }, []);

  const handleConflictDismiss = useCallback(() => {
    // Dismiss the dialog - local changes are preserved in queue
    setConflictDocId(null);
  }, []);

  // Retry handler for save errors
  const handleRetry = useCallback(async () => {
    await retryNow();
  }, [retryNow]);

  const handleFileImport = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        // 1️⃣ Show overlay and start import process (0%)
        setShowImportOverlay(true);
        setImportProgress(0);
        setImportProgressText(t('editorPage.import.progress.reading'));

        // 2️⃣ Read file content (0% → 25%)
        const content = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const text = ev.target?.result as string;
            if (!text || text.trim().length === 0) {
              reject(new Error(t('editorPage.import.emptyFile')));
              return;
            }
            resolve(text);
          };
          reader.onerror = () => reject(new Error(t('editorPage.import.readError')));
          reader.readAsText(file);
        });
        setImportProgress(25);

        // 3️⃣ Clear save queue and sync with server (25% → 50%)
        setImportProgressText(t('editorPage.import.progress.preparing'));
        // eslint-disable-next-line no-console -- Info log for import flow tracking
        console.info('[EditorPage] Clearing save queue before import');
        await clearQueue(docId);

        // 4️⃣ Fetch latest version from server (cloud mode only)
        if (isCloudMode) {
          // eslint-disable-next-line no-console -- Info log for import flow tracking
          console.info('[EditorPage] Fetching latest version before import');
          const apiClient = new DocApiClient(
            import.meta.env.VITE_API_BASE_URL || 'https://api-staging.getlinkode.com',
            getAuthToken
          );
          const latestDoc = await apiClient.getDoc(docId);

          // 5️⃣ Update baseVersionId to server's latest version
          setBaseVersionId(latestDoc.versionId);
        }
        setImportProgress(50);

        // 6️⃣ Update editor content (50% → 75%)
        setImportProgressText(t('editorPage.import.progress.importing'));
        editorRef.current?.setMarkdown(content);
        setImportProgress(75);

        // 7️⃣ Mark that the next save is triggered by import (75% → 100%)
        setImportProgressText(t('editorPage.import.progress.saving'));
        isImportTriggeredSaveRef.current = true;

        // Note: Editor's onChange will trigger handleSave automatically
        // Overlay will be hidden in handleSaveSuccess when save completes (100%)
      } catch (error) {
        console.error('[EditorPage] File import failed:', error);

        // Hide overlay on error
        setShowImportOverlay(false);
        setImportProgress(0);

        // Show error toast
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error(t('editorPage.import.failed'));
        }
      } finally {
        // Clear input value (allow re-importing same file)
        e.target.value = '';
      }
    },
    [docId, isCloudMode, clearQueue, getAuthToken, t]
  );

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Title editing handlers
  const handleStartEditTitle = useCallback(() => {
    setEditingTitleValue(docTitle);
    setIsEditingTitle(true);
    // Focus input after state update
    setTimeout(() => titleInputRef.current?.focus(), 0);
  }, [docTitle]);

  const handleSaveTitle = useCallback(async () => {
    const trimmedTitle = editingTitleValue.trim();
    if (trimmedTitle && trimmedTitle !== docTitle) {
      setDocTitle(trimmedTitle);
      // Save title to API in cloud mode
      if (isCloudMode && editorRef.current) {
        const currentMarkdown = editorRef.current.getMarkdown();
        save(docId, currentMarkdown, baseVersionId, { title: trimmedTitle });
      }
    }
    setIsEditingTitle(false);
  }, [editingTitleValue, docTitle, isCloudMode, docId, baseVersionId, save]);

  const handleCancelEditTitle = useCallback(() => {
    setIsEditingTitle(false);
    setEditingTitleValue('');
  }, []);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSaveTitle();
      } else if (e.key === 'Escape') {
        handleCancelEditTitle();
      }
    },
    [handleSaveTitle, handleCancelEditTitle]
  );

  // Navigate back to dashboard
  const handleBackToDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const getCurrentFontFamily = () => {
    return FONT_FAMILIES[fontStyle] || `"${fontStyle}", sans-serif`;
  };

  const markdown = lastChange?.markdown ?? initialContent;

  return (
    <div
      className="landing-base h-screen flex flex-col overflow-hidden"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      {/* Dynamic Styles */}
      {/* eslint-disable-next-line react/jsx-no-literals -- CSS styles */}
      <style>{`
        .markdown-preview {
          font-family: ${getCurrentFontFamily()};
          font-size: ${fontSize}px;
          line-height: 1.6;
          color: var(--color-text-primary);
        }
        .markdown-preview h1 { font-size: 2em; padding-bottom: 0.3em; border-bottom: 1px solid var(--border-color); margin-top: 0; margin-bottom: 16px; font-weight: 600; }
        .markdown-preview h2 { font-size: 1.5em; padding-bottom: 0.3em; border-bottom: 1px solid var(--border-color); margin-top: 24px; margin-bottom: 16px; font-weight: 600; }
        .markdown-preview h3 { font-size: 1.25em; margin-top: 24px; margin-bottom: 16px; font-weight: 600; }
        .markdown-preview p { margin-top: 0; margin-bottom: 16px; text-align: justify; }
        .markdown-preview ul, .markdown-preview ol { padding-left: 2em; margin-top: 0; margin-bottom: 16px; }
        .markdown-preview blockquote { padding: 0 1em; color: var(--color-text-secondary); border-left: 0.25em solid var(--border-color); margin: 0 0 16px 0; }
        .markdown-preview pre { background-color: var(--color-background-light); padding: 16px; border-radius: var(--radius-sm); overflow: auto; margin-bottom: 16px; }
        .markdown-preview code { font-family: var(--font-mono); font-size: 85%; }
        .markdown-preview :not(pre) > code { padding: 0.2em 0.4em; background-color: var(--color-background-light); border-radius: var(--radius-sm); }
        .markdown-preview table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
        .markdown-preview th, .markdown-preview td { padding: 6px 13px; border: 1px solid var(--border-color); }
        .markdown-preview tr:nth-child(2n) { background-color: var(--color-background-light); }
        .markdown-preview hr { height: var(--border-width); padding: 0; margin: 24px 0; background-color: var(--border-color); border: 0; }
        .a4-page {
          width: 210mm;
          min-height: 297mm;
          padding: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
          background: var(--color-white);
          box-shadow: var(--shadow-offset);
          box-sizing: border-box;
          border: var(--border-width) solid var(--border-color);
        }
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white; margin: 0; padding: 0; }
          .no-print { display: none !important; }
          .a4-page { box-shadow: none; margin: 0; width: 100%; min-height: 100vh; border: none; }
        }
        /* Editor toolbar button styles */
        .editor-toolbar-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 6px 12px;
          border: var(--border-width) solid var(--border-color);
          border-radius: var(--radius-sm);
          font-family: var(--font-mono);
          font-size: 12px;
          background: transparent;
          color: var(--color-text-primary);
          cursor: pointer;
          transition: var(--transition-normal);
          gap: 4px;
        }
        .editor-toolbar-btn:hover {
          background: var(--color-text-primary);
          color: var(--color-white);
        }
        .editor-toolbar-btn.active {
          background: var(--color-primary);
          color: var(--color-text-primary);
        }
        .editor-icon-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 6px;
          border: var(--border-width) solid var(--border-color);
          border-radius: var(--radius-sm);
          background: transparent;
          color: var(--color-text-primary);
          cursor: pointer;
          transition: var(--transition-normal);
        }
        .editor-icon-btn:hover {
          background: var(--color-text-primary);
          color: var(--color-white);
        }
      `}</style>

      {/* Header */}
      <header
        className="no-print py-2 px-4 flex justify-between items-center z-30 shrink-0 h-14 relative"
        style={{
          backgroundColor: 'var(--color-background-light)',
          borderBottom: 'var(--border-width) solid var(--border-color)',
        }}
      >
        {/* Left: Back + Logo + Title */}
        <div className="flex items-center gap-2">
          {/* Back to Dashboard Button */}
          <button
            onClick={handleBackToDashboard}
            className="p-1.5 transition-colors hover:bg-gray-100"
            style={{
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-text-secondary)',
            }}
            title={t('editorPage.backToDashboard')}
          >
            <ArrowLeft size={18} />
          </button>

          {/* Logo */}
          <div
            className="p-1.5"
            style={{
              backgroundColor: 'var(--color-primary)',
              border: 'var(--border-width) solid var(--border-color)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <FileText size={18} style={{ color: 'var(--color-text-primary)' }} />
          </div>

          {/* Document Title (Editable) */}
          <div className="flex items-center gap-1">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                value={editingTitleValue}
                onChange={(e) => setEditingTitleValue(e.target.value)}
                onBlur={handleSaveTitle}
                onKeyDown={handleTitleKeyDown}
                className="font-bold text-sm md:text-base px-2 py-0.5 border rounded"
                style={{
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--color-text-primary)',
                  borderColor: 'var(--color-primary)',
                  outline: 'none',
                  minWidth: '120px',
                  maxWidth: '300px',
                }}
              />
            ) : (
              <button
                onClick={handleStartEditTitle}
                className="flex items-center gap-1 font-bold text-sm md:text-base whitespace-nowrap px-2 py-0.5 rounded transition-colors hover:bg-gray-100"
                style={{
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--color-text-primary)',
                }}
                title={t('editorPage.editTitle')}
              >
                <span className="truncate max-w-[200px]">{docTitle}</span>
                <Pencil size={12} style={{ color: 'var(--color-text-secondary)' }} />
              </button>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 md:gap-4 flex-1 justify-end">
          {/* Realtime Connection Status Indicator (SPEC-007) */}
          {isCloudMode && isAuthenticated && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 text-xs"
              style={{
                fontFamily: 'var(--font-mono)',
                color:
                  realtimeState === 'connected'
                    ? 'var(--color-accent-teal)'
                    : realtimeState === 'connecting' || realtimeState === 'reconnecting'
                      ? 'var(--color-text-secondary)'
                      : 'var(--color-accent-red)',
                backgroundColor: 'var(--color-background)',
                border: 'var(--border-width) solid var(--border-color)',
                borderRadius: 'var(--radius-sm)',
              }}
              title={
                realtimeError
                  ? `${t('editorPage.realtime.error')}: ${realtimeError.message}`
                  : isDegraded
                    ? t('editorPage.realtime.degraded')
                    : t(`editorPage.realtime.${realtimeState}`)
              }
            >
              {realtimeState === 'connected' ? (
                <Wifi size={14} />
              ) : realtimeState === 'connecting' || realtimeState === 'reconnecting' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <WifiOff size={14} />
              )}
              <span className="hidden sm:inline">
                {realtimeState === 'connected'
                  ? t('editorPage.realtime.connected')
                  : realtimeState === 'connecting'
                    ? t('editorPage.realtime.connecting')
                    : realtimeState === 'reconnecting'
                      ? t('editorPage.realtime.reconnecting')
                      : t('editorPage.realtime.disconnected')}
              </span>
            </div>
          )}

          {/* Participant List (SPEC-007) - 顯示房間內的參與者 */}
          {isCloudMode && isAuthenticated && realtimeState === 'connected' && (
            <ParticipantList
              participants={participants}
              localDisplayName={email?.split('@')[0] ?? 'You'}
              localColorId={connection?.getLocalPresence()?.colorId ?? 0}
              compact
            />
          )}

          {/* Save Status Indicator - Google Docs style (SPEC-006) */}
          {isCloudMode && (
            <SaveStatusIndicator
              status={saveState.status}
              onClick={saveState.status === 'error' ? handleRetry : undefined}
            />
          )}

          {/* Font Style & Size */}
          <div
            className="flex items-center gap-2 p-0.5 h-8 relative"
            style={{
              backgroundColor: 'var(--color-background)',
              border: 'var(--border-width) solid var(--border-color)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {/* Font Style Dropdown */}
            <div
              className="relative h-full"
              style={{ borderRight: '1px solid var(--border-color)', paddingRight: '4px' }}
            >
              <button
                onClick={() => setShowFontStyleMenu(!showFontStyleMenu)}
                className="flex items-center justify-between gap-2 h-full px-2 text-xs transition-colors min-w-[100px]"
                style={{
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--color-text-primary)',
                  borderRadius: 'var(--radius-sm)',
                }}
                title={t('editorPage.fontStyle.label')}
              >
                <span className="truncate max-w-[80px]">
                  {t(FONT_STYLE_KEYS[fontStyle as FontKey])}
                </span>
                <ChevronDown
                  size={12}
                  style={{
                    color: 'var(--color-text-secondary)',
                    transition: 'var(--transition-normal)',
                  }}
                  className={`flex-shrink-0 ${showFontStyleMenu ? 'rotate-180' : ''}`}
                />
              </button>
              {showFontStyleMenu && (
                <div
                  className="absolute top-full left-0 mt-2 w-56 py-1 z-50"
                  style={{
                    backgroundColor: 'var(--color-white)',
                    border: 'var(--border-width) solid var(--border-color)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: 'var(--shadow-hover)',
                  }}
                >
                  <div
                    className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
                    style={{
                      color: 'var(--color-text-secondary)',
                      borderBottom: '1px solid var(--border-color)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {t('editorPage.fontStyle.defaultFonts')}
                  </div>
                  {FONT_KEYS.map((key) => (
                    <button
                      key={key}
                      onClick={() => {
                        setFontStyle(key);
                        setShowFontStyleMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs flex items-center justify-between"
                      style={{
                        color:
                          fontStyle === key
                            ? 'var(--color-text-primary)'
                            : 'var(--color-text-primary)',
                        backgroundColor:
                          fontStyle === key ? 'var(--color-bg-blue-light)' : 'transparent',
                        transition: 'var(--transition-normal)',
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium" style={{ fontFamily: 'var(--font-mono)' }}>
                          {t(FONT_STYLE_KEYS[key])}
                        </span>
                        <span
                          className="text-[10px] opacity-70 mt-0.5"
                          style={{ fontFamily: FONT_FAMILIES[key] }}
                        >
                          {t('editorPage.fontStyle.preview')}
                        </span>
                      </div>
                      {fontStyle === key && (
                        <Check size={14} style={{ color: 'var(--color-primary)' }} />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Font Size */}
            <div className="flex items-center">
              <button
                onClick={() => setFontSize((s) => Math.max(12, s - 1))}
                className="editor-icon-btn"
                style={{ padding: '4px', border: 'none' }}
                title={t('editorPage.fontSize.decrease')}
              >
                <Minus size={12} />
              </button>
              <span
                className="w-6 text-center text-xs select-none"
                style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-primary)' }}
              >
                {fontSize}
              </span>
              <button
                onClick={() => setFontSize((s) => Math.min(24, s + 1))}
                className="editor-icon-btn"
                style={{ padding: '4px', border: 'none' }}
                title={t('editorPage.fontSize.increase')}
              >
                <Plus size={12} />
              </button>
            </div>
          </div>

          <div
            className="w-px h-6 mx-1 hidden sm:block"
            style={{ backgroundColor: 'var(--border-color)' }}
          />

          {/* View Mode Toggle Group */}
          <div
            className="flex items-center gap-0.5 p-0.5 h-8"
            style={{
              backgroundColor: 'var(--color-background)',
              border: 'var(--border-width) solid var(--border-color)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <button
              onClick={() => updateViewMode('both')}
              className={`px-2 h-full flex items-center justify-center transition-all ${
                viewMode === 'both' ? 'active' : ''
              }`}
              style={{
                backgroundColor: viewMode === 'both' ? 'var(--color-primary)' : 'transparent',
                color:
                  viewMode === 'both' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                borderRadius: 'var(--radius-xs)',
                border: 'none',
              }}
              title={t('editorPage.viewMode.both')}
            >
              <Columns2 size={16} />
            </button>
            <button
              onClick={() => updateViewMode('editor-only')}
              className={`px-2 h-full flex items-center justify-center transition-all ${
                viewMode === 'editor-only' ? 'active' : ''
              }`}
              style={{
                backgroundColor:
                  viewMode === 'editor-only' ? 'var(--color-primary)' : 'transparent',
                color:
                  viewMode === 'editor-only'
                    ? 'var(--color-text-primary)'
                    : 'var(--color-text-secondary)',
                borderRadius: 'var(--radius-xs)',
                border: 'none',
              }}
              title={t('editorPage.viewMode.editorOnly')}
            >
              <Code size={16} />
            </button>
            <button
              onClick={() => updateViewMode('preview-only')}
              className={`px-2 h-full flex items-center justify-center transition-all ${
                viewMode === 'preview-only' ? 'active' : ''
              }`}
              style={{
                backgroundColor:
                  viewMode === 'preview-only' ? 'var(--color-primary)' : 'transparent',
                color:
                  viewMode === 'preview-only'
                    ? 'var(--color-text-primary)'
                    : 'var(--color-text-secondary)',
                borderRadius: 'var(--radius-xs)',
                border: 'none',
              }}
              title={t('editorPage.viewMode.previewOnly')}
            >
              <Eye size={16} />
            </button>
          </div>

          {/* File Import */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileImport}
            accept=".md,.markdown,.txt"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="editor-icon-btn"
            title={t('editorPage.actions.import')}
            disabled={showImportOverlay || !isCloudMode}
          >
            <Upload size={18} />
          </button>

          {/* Print */}
          <button
            onClick={handlePrint}
            className="editor-icon-btn"
            style={{ color: 'var(--color-accent-teal)' }}
            title={t('editorPage.actions.print')}
          >
            <Printer size={18} />
          </button>

          {/* SPEC-019: AI Button */}
          {isCloudMode && isAuthenticated && (
            <div className="relative" ref={aiButtonTriggerRef as React.RefObject<HTMLDivElement>}>
              <AIButtonTrigger
                state={aiButtonState}
                size="md"
                suggestionCount={aiSuggestionCount}
                isExpanded={isAiPanelOpen}
                onClick={handleAiButtonClick}
                aria-label={t('ai.button.label')}
                title={`${t('ai.button.label')} (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Shift+A)`}
              />

              {/* AI Suggestion Panel */}
              <SuggestionPanel
                isOpen={isAiPanelOpen}
                displayMode={aiDisplayMode}
                isLoading={aiState.isLoading}
                error={aiState.error ?? undefined}
                degraded={aiState.degraded}
                degradeReason={
                  aiState.degradeReason as
                    | 'vector_down'
                    | 'graph_empty'
                    | 'quota'
                    | 'other'
                    | 'vector_not_configured'
                    | 'vector_connection_failed'
                    | 'insufficient_docs'
                    | undefined
                }
                hint={aiState.hint}
                backlinks={aiState.backlinks}
                tags={aiState.tags}
                selectedBacklinks={aiState.selectedBacklinks}
                selectedTags={aiState.selectedTags}
                feedbackState={aiState.feedback}
                onToggleBacklink={aiActions.toggleBacklink}
                onToggleTag={aiActions.toggleTag}
                onSelectAllBacklinks={() => {
                  aiState.backlinks.forEach((bl) => {
                    if (!aiState.selectedBacklinks.has(bl.docId)) {
                      aiActions.toggleBacklink(bl.docId);
                    }
                  });
                }}
                onSelectAllTags={() => {
                  aiState.tags.forEach((tg) => {
                    if (!aiState.selectedTags.has(tg.tag)) {
                      aiActions.toggleTag(tg.tag);
                    }
                  });
                }}
                onClearBacklinks={() => {
                  aiState.selectedBacklinks.forEach((docId) => {
                    aiActions.toggleBacklink(docId);
                  });
                }}
                onClearTags={() => {
                  aiState.selectedTags.forEach((tag) => {
                    aiActions.toggleTag(tag);
                  });
                }}
                onApply={handleAiApply}
                onClose={handleAiPanelClose}
                onFeedback={handleAiFeedback}
                onRetry={handleAiRetry}
                triggerRef={aiButtonTriggerRef}
              />
            </div>
          )}

          {/* Account Menu */}
          <div className="relative ml-1">
            <button
              onClick={() => setShowAccountMenu(!showAccountMenu)}
              className="flex items-center gap-1.5 pl-1 pr-1 py-1 transition-colors group"
              style={{ borderRadius: 'var(--radius-sm)' }}
            >
              <div
                className="w-8 h-8 flex items-center justify-center text-xs font-bold"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  border: 'var(--border-width) solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {isAuthenticated && email
                  ? email.charAt(0).toUpperCase()
                  : t('editorPage.account.guest').charAt(0)}
              </div>
              <ChevronDown size={14} style={{ color: 'var(--color-text-secondary)' }} />
            </button>
            {showAccountMenu && (
              <div
                className="absolute top-full right-0 mt-2 w-56 py-1 z-50"
                style={{
                  backgroundColor: 'var(--color-white)',
                  border: 'var(--border-width) solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  boxShadow: 'var(--shadow-hover)',
                }}
              >
                <div
                  className="px-4 py-3"
                  style={{
                    borderBottom: '1px solid var(--border-color)',
                    backgroundColor: 'var(--color-background-light)',
                  }}
                >
                  <p
                    className="text-sm font-semibold"
                    style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-mono)' }}
                  >
                    {isAuthenticated && email ? email.split('@')[0] : t('editorPage.account.guest')}
                  </p>
                  <p className="text-xs truncate" style={{ color: 'var(--color-text-secondary)' }}>
                    {isAuthenticated && email ? email : t('editorPage.account.guestEmail')}
                  </p>
                </div>
                <div className="py-1">
                  <button
                    className="w-full text-left px-4 py-2 text-sm flex items-center gap-3"
                    style={{
                      color: 'var(--color-text-primary)',
                      transition: 'var(--transition-normal)',
                    }}
                  >
                    <User size={16} style={{ color: 'var(--color-text-secondary)' }} />
                    {t('editorPage.account.profile')}
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-sm flex items-center gap-3"
                    style={{
                      color: 'var(--color-text-primary)',
                      transition: 'var(--transition-normal)',
                    }}
                  >
                    <CreditCard size={16} style={{ color: 'var(--color-text-secondary)' }} />
                    {t('editorPage.account.subscription')}
                  </button>
                </div>
                <div className="py-1" style={{ borderTop: '1px solid var(--border-color)' }}>
                  <button
                    onClick={async () => {
                      setShowAccountMenu(false);
                      await useAuthStore.getState().logout();
                      window.location.href = '/';
                    }}
                    className="w-full text-left px-4 py-2 text-sm flex items-center gap-3"
                    style={{
                      color: 'var(--color-accent-red)',
                      transition: 'var(--transition-normal)',
                    }}
                  >
                    <LogOut size={16} style={{ color: 'var(--color-accent-red)' }} />
                    {t('editorPage.account.logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Backdrop for dropdowns */}
        {(showAccountMenu || showFontStyleMenu) && (
          <div
            className="fixed inset-0 z-40 bg-transparent"
            onClick={() => {
              setShowAccountMenu(false);
              setShowFontStyleMenu(false);
            }}
          />
        )}
      </header>

      {/* Load Error UI */}
      {loadError && (
        <div
          className="absolute inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'var(--color-background)' }}
        >
          <Card hoverable={false} className="border-linkode-red bg-white">
            <div className="text-center max-w-md">
              {/* eslint-disable-next-line react/jsx-no-literals -- Emoji symbol */}
              <div className="text-5xl mb-4" style={{ color: 'var(--color-accent-red)' }}>
                ⚠️
              </div>
              <h2
                className="text-xl font-bold mb-2"
                style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-primary)' }}
              >
                {t('editorPage.error.title')}
              </h2>
              <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
                {loadError}
              </p>
              <Button variant="primary" onClick={() => window.location.reload()}>
                {t('editorPage.error.retry')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Editor Panel */}
        <div
          className={`no-print flex flex-col shrink-0 transition-all duration-300 ease-in-out ${
            viewMode === 'preview-only'
              ? 'w-0 opacity-0 overflow-hidden'
              : viewMode === 'editor-only'
                ? 'flex-1'
                : 'w-96 md:w-2/5 opacity-100'
          }`}
          style={{
            backgroundColor: 'var(--color-white)',
            borderRight:
              viewMode !== 'preview-only'
                ? 'var(--border-width) solid var(--border-color)'
                : 'none',
          }}
        >
          <EditorShell
            key={editorShellKey}
            ref={editorRef}
            docId={docId}
            initialContent={effectiveInitialContent}
            mode="plain"
            baseVersionId={baseVersionId}
            isOffline={!saveState.isOnline || isYjsOffline}
            isLoading={isLoading}
            onChange={handleChange}
            onSelectionChange={handleSelectionChange}
            onSave={isCloudMode ? handleSave : undefined}
            className="flex-1"
            additionalExtensions={yjsExtension ? [yjsExtension] : undefined}
          />
        </div>

        {/* Preview Panel */}
        <div
          className={`flex flex-col relative overflow-hidden transition-all duration-300 ease-in-out ${
            viewMode === 'editor-only' ? 'w-0 opacity-0 overflow-hidden' : 'flex-1'
          }`}
          style={{ backgroundColor: 'var(--color-background)' }}
        >
          {/* Horizontal Ruler */}
          <div
            className="no-print h-8 shrink-0 relative flex justify-center z-20"
            style={{
              backgroundColor: 'var(--color-background-light)',
              borderBottom: '1px solid var(--border-color)',
            }}
          >
            <div
              className="h-full relative"
              style={{
                width: `${A4_WIDTH_MM * MM_TO_PX}px`,
                backgroundColor: 'var(--color-white)',
                borderLeft: '1px solid var(--border-color)',
                borderRight: '1px solid var(--border-color)',
              }}
            >
              {/* Ruler Marks */}
              {Array.from({ length: 22 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute bottom-0 h-2 text-[8px] pl-0.5"
                  style={{
                    left: `${i * 10 * MM_TO_PX}px`,
                    borderLeft: '1px solid var(--border-color)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {i > 0 && i % 2 === 0 ? i : ''}
                </div>
              ))}
              {/* Left Margin Indicator */}
              <div
                className="absolute top-0 left-0 h-full"
                style={{
                  width: `${margins.left * MM_TO_PX}px`,
                  backgroundColor: 'var(--color-primary-light)',
                  borderRight: '1px solid var(--color-primary)',
                }}
              />
              {/* Right Margin Indicator */}
              <div
                className="absolute top-0 right-0 h-full"
                style={{
                  width: `${margins.right * MM_TO_PX}px`,
                  backgroundColor: 'var(--color-primary-light)',
                  borderLeft: '1px solid var(--color-primary)',
                }}
              />
            </div>
          </div>

          {/* Preview Area */}
          <div className="flex flex-1 overflow-hidden relative">
            {/* Vertical Ruler */}
            <div
              className="no-print w-8 shrink-0 relative overflow-hidden flex flex-col items-end z-20"
              style={{
                backgroundColor: 'var(--color-background-light)',
                borderRight: '1px solid var(--border-color)',
              }}
            >
              <div
                className="w-full relative mb-2 shrink-0"
                style={{
                  height: `${A4_HEIGHT_MM * MM_TO_PX}px`,
                  backgroundColor: 'var(--color-white)',
                }}
              >
                {Array.from({ length: 30 }).map((_, i) => (
                  <div
                    key={i}
                    className="absolute right-0 w-2 text-[8px] pt-0.5 pr-0.5 text-right"
                    style={{
                      top: `${i * 10 * MM_TO_PX}px`,
                      borderTop: '1px solid var(--border-color)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {i > 0 && i % 5 === 0 ? i : ''}
                  </div>
                ))}
                {/* Top Margin Indicator */}
                <div
                  className="absolute top-0 right-0 w-full"
                  style={{
                    height: `${margins.top * MM_TO_PX}px`,
                    backgroundColor: 'var(--color-primary-light)',
                    borderBottom: '1px solid var(--color-primary)',
                  }}
                />
                {/* Bottom Margin Indicator */}
                <div
                  className="absolute bottom-0 right-0 w-full"
                  style={{
                    height: `${margins.bottom * MM_TO_PX}px`,
                    backgroundColor: 'var(--color-primary-light)',
                    borderTop: '1px solid var(--color-primary)',
                  }}
                />
              </div>
            </div>

            {/* A4 Preview Container - WYSIWYG Editor */}
            <div
              className="flex-1 overflow-hidden relative flex flex-col"
              style={{ backgroundColor: 'var(--color-background)' }}
            >
              <WysiwygPreviewEditor
                ref={wysiwygPreviewRef}
                initialContent={markdown}
                readonly={false}
                showToolbar={true}
                onChange={handleWysiwygChange}
                participants={participants}
                className="flex-1"
                style={{
                  fontFamily: FONT_FAMILIES[fontStyle] || FONT_FAMILIES['sans'],
                }}
              />
            </div>
          </div>

          {/* Status Bar */}
          <div
            className="no-print h-6 flex items-center justify-between px-4 text-xs shrink-0 z-30"
            style={{
              backgroundColor: 'var(--color-background-light)',
              borderTop: 'var(--border-width) solid var(--border-color)',
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <span>{t('editorPage.statusBar.pageSize')}</span>
            <div className="flex gap-4 items-center">
              <span>
                {t('editorPage.statusBar.cursor')} {selection?.head ?? 0}
              </span>
              <span>
                {t('editorPage.statusBar.wordCount')} {markdown.length}
              </span>
              <span>
                {t('editorPage.statusBar.version')} {lastChange?.versionVector ?? 0}
              </span>
            </div>
            <span>
              {/* eslint-disable-next-line react/jsx-no-literals -- Slash symbol for margins display */}
              {t('editorPage.statusBar.margins')} {margins.top}/{margins.bottom}{' '}
              {/* eslint-disable-next-line react/jsx-no-literals -- Slash symbol for margins display */}
              {t('editorPage.statusBar.topBottom')} {margins.left}/{margins.right}{' '}
              {t('editorPage.statusBar.leftRight')}
            </span>
          </div>
        </div>
      </div>

      {/* Import Overlay with Progress */}
      {showImportOverlay && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
          }}
        >
          <Card
            style={{
              padding: '2rem',
              minWidth: '320px',
              textAlign: 'center',
            }}
          >
            <div className="flex flex-col items-center gap-4">
              {/* Spinner */}
              <Loader2
                size={48}
                className="animate-spin"
                style={{ color: 'var(--color-accent-teal)' }}
              />

              {/* Progress Text */}
              <div>
                <h3
                  style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: 'var(--color-text-primary)',
                    marginBottom: '0.5rem',
                  }}
                >
                  {t('editorPage.actions.importing')}
                </h3>
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {importProgressText}
                </p>
              </div>

              {/* Progress Bar */}
              <div style={{ width: '100%', maxWidth: '300px' }}>
                {/* Background track */}
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: 'var(--color-border)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  {/* Progress fill */}
                  <div
                    style={{
                      width: `${importProgress}%`,
                      height: '100%',
                      backgroundColor: 'var(--color-accent-teal)',
                      transition: 'width 0.3s ease',
                      borderRadius: '4px',
                    }}
                  />
                </div>
                {/* Percentage text */}
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-secondary)',
                    marginTop: '0.5rem',
                    textAlign: 'center',
                  }}
                >
                  {/* eslint-disable-next-line react/jsx-no-literals -- Percent symbol */}
                  {importProgress}%
                </p>
              </div>

              {/* Info Text */}
              <p
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--color-text-tertiary)',
                  maxWidth: '280px',
                }}
              >
                {t('editorPage.import.pleaseWait')}
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Conflict Dialog (SPEC-006) */}
      <ConflictDialog
        isOpen={conflictDocId !== null}
        docId={conflictDocId ?? ''}
        onRefresh={handleConflictRefresh}
        onDismiss={handleConflictDismiss}
      />

      {/* SPEC-019: AI Toast Notifications */}
      {toasts.map((toast: Toast) => (
        <ToastNotification
          key={toast.id}
          type={toast.type}
          message={toast.message}
          position="top-right"
          autoDismiss={toast.autoCloseDelay > 0}
          autoDismissMs={toast.autoCloseDelay}
          showUndo={toast.showUndo}
          onUndo={toast.onUndo}
          onDismiss={() => dismissToast(toast.id)}
        />
      ))}
    </div>
  );
}

export default EditorPage;
